import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { AnalyticsLayout } from '@/components/analytics/AnalyticsLayout'
import { ComparablesForm, ComparablesFormState } from '@/components/analytics/ComparablesForm'
import { ExportButton } from '@/components/shared/ExportButton'
import { ChartInfoButton } from '@/components/shared/ChartInfoButton'
import { ChartChrome } from '@/components/shared/ChartChrome'
import {
  AnalyticsEmptyState,
  AnalyticsErrorBlock,
  AnalyticsLoadingBlock,
} from '@/components/shared/AnalyticsStates'
import { api } from '@/lib/api'
import { euro } from '@/lib/format'

interface ComparableUnit {
  unitId: number
  unitRef: string | null
  projectId: number
  projectName: string | null
  city: string | null
  district: string | null
  typology: string | null
  areaM2: number | null
  currentPrice: number | null
  sqmPrice: number | null
  status: string
  listingDate: string | null
  salesDate: string | null
  daysOnMarket: number | null
  distance: number
}

interface ComparablesResponse {
  target: {
    city: string
    district: string | null
    typology: string
    targetSqm: number
    sqmTolerance: number
  }
  stats: {
    count: number
    medianSqmPrice: number | null
    p25SqmPrice: number | null
    p75SqmPrice: number | null
    minSqmPrice: number | null
    maxSqmPrice: number | null
  }
  distribution: Array<{ lower: number; upper: number; count: number }>
  rows: ComparableUnit[]
}

export function Comparables() {
  const [params, setParams] = useState<ComparablesFormState | null>(null)

  const { data, isFetching, error, refetch } = useQuery<ComparablesResponse>({
    queryKey: ['analytics.comparables', params],
    queryFn: async () => {
      // Strip empty-string district so the DTO validator treats it as absent
      const sanitized = params
        ? { ...params, district: params.district || undefined }
        : params
      const res = await api.get('/analytics/comparables', { params: sanitized })
      return res.data
    },
    enabled: !!params,
    staleTime: 5 * 60 * 1000,
  })

  const distroRows = (data?.distribution ?? []).map((b) => ({
    range: `${Math.round(b.lower)}–${Math.round(b.upper)}`,
    count: b.count,
  }))

  return (
    <AnalyticsLayout>
      <div className="space-y-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-2">
            <div>
              <h2 className="text-lg font-semibold text-secondary-900">Comparables</h2>
              <p className="text-xs text-secondary-500">
                CMA-style search. Enter a target city + typology + m², get the closest active and recently-sold
                units ranked by m² proximity, with a €/m² distribution for the comparable set.
              </p>
            </div>
            <ChartInfoButton
              title="Comparables"
              info={{
                definition:
                  'CMA-style (comparative market analysis) search. Given a target city + typology + m², returns the live and recently-sold units closest to the target size, plus a €/m² distribution over that comparable set. The primary underwriting tool for pricing a new launch or validating a land bid.',
                formula:
                  'Candidates: unit WHERE city = target.city AND typology = target.typology AND COALESCE(private_gpa, total_gpa) BETWEEN target.sqm × (1 − tol) AND target.sqm × (1 + tol) · distance = |COALESCE(unit.private_gpa, unit.total_gpa) − target.sqm| · stats over sqm_price (median, Q1, Q3, min, max)',
                calculation:
                  'District is optional — when provided it narrows to same-district comps. Status filters (available / reserved / sold) are applied by the form. Sorted by absolute m² proximity ascending so the closest comps appear first. The distribution histogram buckets €/m² into fixed-width bins over the matched set. The area match prefers private_gpa (Blue Tagus canonical denominator) and falls back to total_gpa when a broker does not publish private area, so those units still participate; the €/m² math itself always uses private area (enforced at DB level — sqm_price is NULL when private_gpa is NULL).',
                source: 'unit.city, unit.typology, unit.private_gpa, unit.total_gpa, unit.sqm_price, unit.status, unit.sales_date, unit.listing_date',
              }}
            />
          </div>
          {params && (
            <ExportButton
              endpoint="/analytics/export/comparables.csv"
              params={{
                ...params,
                district: params.district || undefined,
              } as Record<string, string | number | boolean | undefined>}
              filename="analytics-comparables"
            />
          )}
        </div>

        <ComparablesForm onSubmit={setParams} submitting={isFetching} />

        {error && (
          <div className="card">
            <AnalyticsErrorBlock
              title="Comparables search failed"
              error={error}
              onRetry={() => {
                void refetch()
              }}
            />
          </div>
        )}

        {params && !data && !error && isFetching && (
          <div className="card">
            <AnalyticsLoadingBlock height={220} />
          </div>
        )}

        {data && (
          <>
            <div className="card">
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm">
                <Stat label="Matches" value={data.stats.count.toString()} />
                <Stat label="Median €/m²" value={euro(data.stats.medianSqmPrice)} />
                <Stat label="Q1 €/m²" value={euro(data.stats.p25SqmPrice)} />
                <Stat label="Q3 €/m²" value={euro(data.stats.p75SqmPrice)} />
                <Stat
                  label="Range"
                  value={`${euro(data.stats.minSqmPrice)} – ${euro(data.stats.maxSqmPrice)}`}
                />
              </div>
            </div>

            <ChartChrome
              title="€/m² distribution"
              subtitle={`${data.stats.count} matches in the comparable set`}
              info={{
                data: {
                  definition:
                    'Histogram of €/m² across the comparable set. Reveals whether the target sits in a tight range (pricing is defensible) or a spread one (more pricing optionality).',
                  formula:
                    'COUNT(unit) grouped into fixed-width €/m² buckets over the matched candidates',
                  calculation:
                    'Bucket width is auto-derived from the range. A single tall column means pricing is tightly clustered; a spread distribution means comparable stock prices in a wide band.',
                  source: 'unit.sqm_price (comparables set)',
                },
              }}
              bodyMinHeight={220}
              render={(height) => {
                if (distroRows.length === 0) {
                  return (
                    <AnalyticsEmptyState
                      title="No matches yet"
                      hint="No comparable units found for this city / typology / m² target. Try widening the m² tolerance or removing the district filter."
                      minHeight={height}
                    />
                  )
                }
                return (
                  <ResponsiveContainer width="100%" height={height}>
                    <BarChart data={distroRows} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis dataKey="range" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                      <Tooltip />
                      <Bar dataKey="count" fill="#2563eb" />
                    </BarChart>
                  </ResponsiveContainer>
                )
              }}
            />

            <div className="card overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="sticky top-0 z-10 bg-white">
                  <tr className="text-left text-xs uppercase tracking-wide text-secondary-500 border-b">
                    <th className="py-2 pr-4">Unit</th>
                    <th className="py-2 pr-4">Project</th>
                    <th className="py-2 pr-4">City / District</th>
                    <th className="py-2 pr-4 text-right">m²</th>
                    <th className="py-2 pr-4 text-right">€/m²</th>
                    <th className="py-2 pr-4 text-right">Price</th>
                    <th className="py-2 pr-4">Status</th>
                    <th className="py-2 pr-4 text-right">Days</th>
                    <th className="py-2 pr-4 text-right">Δ m²</th>
                  </tr>
                </thead>
                <tbody>
                  {data.rows.map((r) => (
                    <tr key={r.unitId} className="border-b last:border-0 hover:bg-secondary-50">
                      <td className="py-2 pr-4">
                        <Link
                          to={`/units/${r.unitId}`}
                          className="text-primary-600 hover:underline"
                        >
                          {r.unitRef ?? `#${r.unitId}`}
                        </Link>
                      </td>
                      <td className="py-2 pr-4">
                        <Link to={`/projects/${r.projectId}`} className="hover:underline">
                          {r.projectName ?? '—'}
                        </Link>
                      </td>
                      <td className="py-2 pr-4">
                        {r.city ?? '—'}
                        {r.district ? ` / ${r.district}` : ''}
                      </td>
                      <td className="py-2 pr-4 text-right">{r.areaM2 ?? '—'}</td>
                      <td className="py-2 pr-4 text-right">{euro(r.sqmPrice)}</td>
                      <td className="py-2 pr-4 text-right">{euro(r.currentPrice)}</td>
                      <td className="py-2 pr-4">
                        <StatusBadge status={r.status} />
                      </td>
                      <td className="py-2 pr-4 text-right">
                        {r.daysOnMarket == null ? '—' : `${Math.round(r.daysOnMarket)}d`}
                      </td>
                      <td className="py-2 pr-4 text-right text-secondary-500">
                        {r.distance.toFixed(0)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </AnalyticsLayout>
  )
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-xs text-secondary-500">{label}</div>
      <div className="text-base font-semibold text-secondary-900">{value}</div>
    </div>
  )
}

function StatusBadge({ status }: { status: string }) {
  const cls: Record<string, string> = {
    available: 'bg-green-50 text-green-700',
    reserved: 'bg-amber-50 text-amber-700',
    sold: 'bg-slate-100 text-slate-700',
  }
  return (
    <span
      className={`inline-block text-xs px-2 py-0.5 rounded ${
        cls[status] ?? 'bg-secondary-100 text-secondary-700'
      }`}
    >
      {status}
    </span>
  )
}
